import { Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';

export interface SentryErrorContext {
  component?: string;
  method?: string;
  userId?: string;
  userEmail?: string;
  url?: string;
  statusCode?: number;
  // skipcq: JS-0323
  additionalData?: any;
}

@Injectable({
  providedIn: 'root'
})
export class SentryService {
  
  /**
   * Capture an exception with context
   */
  // skipcq: JS-0323
  captureException(error: any, context?: SentryErrorContext): void {
    Sentry.withScope((scope) => {
      if (context) {
        this.addContextToScope(scope, context);
      }
      Sentry.captureException(error);
    });
  }

  /**
   * Capture a message with context
   */
  captureMessage(message: string, level: 'info' | 'warning' | 'error' = 'error', context?: SentryErrorContext): void {
    Sentry.withScope((scope) => {
      if (context) {
        this.addContextToScope(scope, context);
      }
      Sentry.captureMessage(message, level);
    });
  }

  /**
   * Capture HTTP errors with enhanced context
   */
  // skipcq: JS-0323
  captureHttpError(error: any, url: string, statusCode: number, context?: Partial<SentryErrorContext>): void {
    const httpContext: SentryErrorContext = {
      component: 'HTTP',
      method: 'HTTP_REQUEST',
      url,
      statusCode,
      ...context
    };

    this.captureException(error, httpContext);
  }

  /**
   * Capture API response errors
   */
  // skipcq: JS-0323
  captureApiError(error: any, endpoint: string, context?: Partial<SentryErrorContext>): void {
    const apiContext: SentryErrorContext = {
      component: 'API_SERVICE',
      method: 'API_CALL',
      url: endpoint,
      ...context
    };

    this.captureException(error, apiContext);
  }

  /**
   * Set user context for better error tracking
   */
  // skipcq: JS-0323
  setUserContext(userId: string, email?: string, additionalData?: any): void {
    Sentry.setUser({
      id: userId,
      email,
      ...additionalData
    });
  }

  /**
   * Clear user context (useful for logout)
   */
  clearUserContext(): void {
    Sentry.setUser(null);
  }

  /**
   * Add breadcrumb for better error context
   */
  addBreadcrumb(message: string, category = 'custom', level: 'info' | 'warning' | 'error' = 'info'): void {
    Sentry.addBreadcrumb({
      message,
      category,
      level,
      timestamp: Date.now() / 1000
    });
  }

  /**
   * Test Sentry connectivity
   */
  async testConnectivity(): Promise<string> {
    const result = await Sentry.diagnoseSdkConnectivity();
    return result || 'unknown';
  }

  /**
   * Private method to add context to Sentry scope
   */
  // skipcq: JS-0323
  private addContextToScope(scope: any, context: SentryErrorContext): void {
    if (context.component) {
      scope.setTag('component', context.component);
    }
    if (context.method) {
      scope.setTag('method', context.method);
    }
    if (context.userId) {
      scope.setUser({ id: context.userId, email: context.userEmail });
    }
    if (context.url) {
      scope.setContext('request', { url: context.url });
    }
    if (context.statusCode) {
      scope.setTag('status_code', context.statusCode.toString());
    }
    if (context.additionalData) {
      scope.setContext('additional_data', context.additionalData);
    }
  }
}
